import { Fragment, useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import withHeadTag from '../../../common/HeadComponent';
import HubPageLayout from '../../../common/hub/HubPageLayout';
import PageError from '../../../common/PageError';
import SessionsPageContentHeadNav from '../../../components/hub/sessions/SessionsPageContentHeadNav';
import { getPaymentFailureAlertInfo } from '../../../util/helper';
import { PaymentFailureAlertBox } from '../../../components/hub/home/InfoBox';
import SessionFilter from './SessionFilter';
import useSessionContext, { withSessionContextProvider } from './useSessionContext';
import { SESSION_CATEGORY } from './types';
import { InitialState } from '../../../types/hubTypes';
import { isPaymentFailed } from '../../../util/helpers';
import { UpcomingSessions, CompletedSessions, CancelledSessions } from './Categories';

const Sessions = () => {
  const [activeCategory, setActiveCategory] = useState(SESSION_CATEGORY.UPCOMING);
  const { filteredSessions, fetchSessionIsError: errorSessions } = useSessionContext();
  const location = useLocation();
  const { upcoming = [] } = filteredSessions;
  const errorLoading = useSelector((state: InitialState) => state.ui.apiState.hubCompletedSessionsPage.fetchError);

  // Set initial category based on URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && Object.values(SESSION_CATEGORY).includes(hash)) {
      setActiveCategory(hash);
    }
  }, [location.hash]);

  const upcomingSessionsWithFailedPayment = useMemo(
    () => upcoming?.filter((sesh) => isPaymentFailed(sesh.paymentStatus)),
    [upcoming]
  );

  const paymentFailureAlert = useMemo(
    () => getPaymentFailureAlertInfo(upcomingSessionsWithFailedPayment),
    [upcomingSessionsWithFailedPayment]
  );

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  const renderActiveCategory = useMemo(() => {
    switch (activeCategory) {
      case SESSION_CATEGORY.UPCOMING:
        return <UpcomingSessions />;
      case SESSION_CATEGORY.COMPLETED:
        return <CompletedSessions />;
      case SESSION_CATEGORY.CANCELLED:
        return <CancelledSessions />;
      default:
        return null;
    }
  }, [activeCategory]);

  return (
    <HubPageLayout>
      {(errorSessions || errorLoading) && <PageError activePage="hub-page" retryEnabled={false} />}
      {!errorSessions && !errorLoading && (
        <Fragment>
          {paymentFailureAlert && activeCategory === SESSION_CATEGORY.UPCOMING && (
            <div className="mb-lg-11 mb-10">
              <PaymentFailureAlertBox message={paymentFailureAlert} />
            </div>
          )}
          <SessionsPageContentHeadNav activeCategory={activeCategory} onCategoryChange={handleCategoryChange} />
          <SessionFilter filterClassName="pt-3 pb-6 lg:pb-10" />
          {renderActiveCategory}
        </Fragment>
      )}
    </HubPageLayout>
  );
};

export default withSessionContextProvider(withHeadTag(Sessions));
